import { Dispatch, SetStateAction, useState } from "react";
import {
  downloadTransactionReceipt,
  requestChangeSaleReport,
  updateInvoiceComment,
} from "../../../../services";
import { useUserRole } from "../../../../utils/hooks/useUserRole";
import { useCompact } from "../../../../utils/hooks/useCompact";
import { ReplacedBy, SaleStatus } from "../../../../types/sale.type";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import EditIcon from "@mui/icons-material/Edit";
import AddCommentIcon from "@mui/icons-material/AddComment";
import DownloadIcon from "@mui/icons-material/Download";
import CancelActions from "../../CancelActions";

interface FooterCommentProps {
  comment: string;
  uuid: string;
  status: SaleStatus;
  replacedBy?: ReplacedBy | null;
  commentLoading: boolean;
  setCommentLoading: Dispatch<SetStateAction<boolean>>;
  reqChangeLoading: boolean;
  setReqChangeLoading: Dispatch<SetStateAction<boolean>>;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
}

export default function FooterComment({
  comment,
  uuid,
  status,
  replacedBy,
  commentLoading,
  setCommentLoading,
  reqChangeLoading,
  setReqChangeLoading,
  setTriggerFetch,
}: FooterCommentProps) {
  const { isBranchAccounting, isBranchFinance, isBranchFinanceAndAccount } =
    useUserRole();
  const compact = useCompact();
  const [downloadLoading, setDownloadLoading] = useState(false);

  const onDownloadReceipt = async () => {
    try {
      setDownloadLoading(true);
      const response = await downloadTransactionReceipt(uuid);

      const disposition = response.headers["content-disposition"] as
        | string
        | undefined;
      const utf8Match = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
      const plainMatch = disposition?.match(/filename="?([^";]+)"?/i);
      const filename = utf8Match
        ? decodeURIComponent(utf8Match[1])
        : plainMatch
          ? plainMatch[1]
          : `receipt-${uuid}.pdf`;

      const url = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Alert is shown by the global axios error interceptor.
    } finally {
      setDownloadLoading(false);
    }
  };

  const onComment = async () => {
    try {
      setCommentLoading(true);
      await updateInvoiceComment(uuid, comment);
      setTriggerFetch((prev) => prev + 1);
      setCommentLoading(false);
    } catch (error) {
      setCommentLoading(false);
    }
  };

  const onRequestChange = async () => {
    try {
      setReqChangeLoading(true);
      await requestChangeSaleReport(uuid);
      setTriggerFetch((prev) => prev + 1);
      setReqChangeLoading(false);
    } catch (error) {
      setReqChangeLoading(false);
    }
  };

  return (
    <>
      <Paper
        sx={{
          // Offset by the live sidebar width so this fixed bar stays aligned
          // with the main content area instead of sliding under the sidebar.
          left: "var(--gb-sidebar-width, 0px)",
          right: 0,
          height: "66px",
          position: "fixed",
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "24px",
          boxShadow: "0 -4px 24px -8px rgba(0,0,0,0.10)",
          ...(compact && { height: "auto", minHeight: "56px", padding: "12px 16px" }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            ...(compact && { gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }),
          }}
        >
          <CancelActions
            uuid={uuid}
            status={status}
            replacedBy={replacedBy}
            setTriggerFetch={setTriggerFetch}
          />
          <Button
            loading={downloadLoading}
            disabled={commentLoading || reqChangeLoading}
            size={compact ? "medium" : "large"}
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon fontSize="small" />}
            onClick={onDownloadReceipt}
          >
            ดาวน์โหลดรายงาน
          </Button>
          <Button
            loading={commentLoading}
            disabled={reqChangeLoading || downloadLoading}
            size={compact ? "medium" : "large"}
            variant="contained"
            sx={{ bgcolor: "#FDA42C" }}
            startIcon={<AddCommentIcon fontSize="small" />}
            onClick={onComment}
          >
            เพิ่มคอมเม้นต์
          </Button>
          {!isBranchAccounting &&
            !isBranchFinance &&
            !isBranchFinanceAndAccount && (
              <Button
                disabled={commentLoading}
                loading={reqChangeLoading}
                size={compact ? "medium" : "large"}
                variant="contained"
                color="primary"
                startIcon={<EditIcon fontSize="small" />}
                onClick={onRequestChange}
              >
                ขอแก้ไข
              </Button>
            )}
        </Box>
      </Paper>
    </>
  );
}
